import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { ShoppingBag, Star, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

function StarRating({ rating = 4.5, count = 0 }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`h-3 w-3 ${i <= Math.floor(rating) ? 'star-filled fill-current' : 'star-empty'}`} />
      ))}
      {count > 0 && <span className="text-[10px] text-[var(--dark)]/30 ml-0.5">({count})</span>}
    </div>
  );
}

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const hasDiscount = product.discount_price && product.discount_price < product.price;
  const displayPrice = hasDiscount ? product.discount_price : product.price;
  const discountPercent = hasDiscount ? Math.round((1 - product.discount_price / product.price) * 100) : 0;
  const outOfStock = (product.stock !== undefined && product.stock <= 0);

  return (
    <div className={`product-card group bg-white rounded-2xl overflow-hidden border border-[var(--sage)]/15 hover:border-[var(--violet)]/20 ${outOfStock ? 'opacity-75' : ''}`} data-testid={`product-card-${product.id}`}>
      {/* Image */}
      <Link to={`/producto/${product.id}`} className="block relative aspect-[4/5] overflow-hidden bg-[var(--cream)]">
        <img
          src={product.images?.[0] || '/placeholder.jpg'}
          alt={product.name}
          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${outOfStock ? 'grayscale-[30%]' : ''}`}
          loading="lazy"
        />
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {outOfStock && (
            <Badge className="bg-red-500 text-white border-0 text-[10px] font-bold px-2 py-0.5 rounded-md" data-testid="out-of-stock-badge">
              Agotado
            </Badge>
          )}
          {hasDiscount && !outOfStock && (
            <Badge className="bg-[var(--violet)] text-white border-0 text-[10px] font-bold px-2 py-0.5 rounded-md" data-testid="discount-badge">
              -{discountPercent}%
            </Badge>
          )}
          {product.featured && !outOfStock && (
            <Badge className="bg-[var(--sage)] text-[var(--dark)] border-0 text-[10px] px-2 py-0.5 rounded-md" data-testid="featured-badge">
              Best Seller
            </Badge>
          )}
          {product.stock > 0 && product.stock <= 5 && (
            <Badge className="bg-orange-500 text-white border-0 text-[10px] font-bold px-2 py-0.5 rounded-md" data-testid="low-stock-badge">
              Quedan {product.stock}
            </Badge>
          )}
        </div>
        {/* Quick actions overlay */}
        {!outOfStock && (
          <div className="product-overlay">
            <div className="flex gap-2">
              <Button
                size="sm"
                className="bg-white/95 text-[var(--dark)] hover:bg-[var(--violet)] hover:text-white rounded-full h-9 px-4 text-xs font-semibold shadow-lg backdrop-blur-sm transition-all"
                onClick={(e) => { e.preventDefault(); addItem(product); }}
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag className="h-3.5 w-3.5 mr-1.5" /> Agregar
              </Button>
              <Button
                size="icon"
                className="bg-white/95 text-[var(--dark)] hover:bg-[var(--violet)] hover:text-white rounded-full h-9 w-9 shadow-lg backdrop-blur-sm transition-all"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `/producto/${product.id}`; }}
              >
                <Eye className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </Link>

      {/* Info */}
      <div className="p-4 space-y-1.5">
        <p className="text-[10px] text-[var(--violet)] font-semibold tracking-widest uppercase">{product.brand}</p>
        <Link to={`/producto/${product.id}`}>
          <h3 className="text-[13px] font-semibold text-[var(--dark)] leading-snug line-clamp-2 hover:text-[var(--violet)] transition-colors min-h-[36px]" data-testid="product-name">
            {product.name}
          </h3>
        </Link>
        <StarRating rating={4.5} count={Math.floor(Math.random() * 30) + 5} />
        <div className="flex items-baseline gap-2 pt-1">
          {hasDiscount && (
            <span className="text-xs text-[var(--dark)]/30 line-through" data-testid="original-price">${product.price?.toFixed(2)}</span>
          )}
          <span className={`text-base font-bold ${hasDiscount ? 'text-[var(--violet)]' : 'text-[var(--dark)]'}`} data-testid="product-price">
            ${displayPrice?.toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
}
